import { inject, Injectable, signal } from '@angular/core';
import { RawMaterialsFe, RawMaterialsFields, RawMaterialsForm, RawMaterialsFormGroups, RawMaterialsValidators } from '../raw-materials.model';
import { CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { RawMaterials, Suppliers } from 'tilby-models';
import { SupplierCodes } from 'src/app/features/items/utils/item.interface';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';


const RequireMatch = <T extends { code: string, name: string }>(controlList: T[]): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors|null => {
        const selection: any = control.value?.toString() || '';
        if (controlList.map(({name}) => name).indexOf(selection) > -1 ||
         controlList.map(({code}) => code).indexOf(selection) > -1 || selection === '' ) {
            return null;
        }
        return {matchRequired: true};
    }
}

@Injectable()
export class RawMaterialDetailsFormService {

  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  
  private rawMaterialForm!: RawMaterialsFormGroups;
  rawMaterialFormReady = signal(false);

  get form() {
    return this.rawMaterialForm;
  }
    
  supplierCodes: Array<SupplierCodes> = new Array<SupplierCodes>();

  validators: RawMaterialsValidators = {
    nameMaxLength: 255,
    alertQuantityMinValue: 0,
    unitMaxLength: 10,
    costMinValue: 0,

    // --------
    provMinLength: 0,
    provMaxLength: 255,
    provMaxLengthArray: new Array(10).fill(2)
  };

  createForm(rawMaterial: RawMaterialsFields = new RawMaterialsFe() ,hiddenFields?:Array<keyof RawMaterialsFields>) {
    this.supplierCodes = this.getSupplierCodes();

    this.rawMaterialForm = new CustomFormGroup<RawMaterialsForm>({
      id: new CustomFormControl(
        rawMaterial.id || null
      ),
      name: new CustomFormControl(
        rawMaterial.name || null,
        { validators: [Validators.required, Validators.maxLength(this.validators.nameMaxLength)] }
      ),
      quantity_alert: new CustomFormControl(
        rawMaterial.quantity_alert != null ? rawMaterial.quantity_alert : null
      ),
      unit: new CustomFormControl(
        rawMaterial.unit || null,
        { validators: [Validators.maxLength(this.validators.unitMaxLength)] }
      ),
      cost: new CustomFormControl(
        rawMaterial.cost != null ? rawMaterial.cost : null
      ),
      default_supplier_id: new CustomFormControl(
        rawMaterial.default_supplier_id || null,
        { validators: RequireMatch(this.supplierCodes) }
      ),
      stock_type: new CustomFormControl(
        rawMaterial.stock_type || null
      ),
      auto_unload: new CustomFormControl(
        rawMaterial.auto_unload || null
      )
    });

    this.rawMaterialFormReady.set(true);
    return this.rawMaterialForm;
  }

  resetFormValues(rawMaterial: RawMaterials = new RawMaterialsFe()) {
    this.rawMaterialForm.patchValue({
      id: rawMaterial.id || null,
      name: rawMaterial.name || null,
      quantity_alert: rawMaterial.quantity_alert != null ? rawMaterial.quantity_alert : null,
      unit: rawMaterial.unit || null,
      cost: rawMaterial.cost != null ? rawMaterial.cost : null,
      default_supplier_id: rawMaterial.default_supplier_id || null,
      stock_type: rawMaterial.stock_type || null,
      auto_unload: rawMaterial.auto_unload || null
    });

    this.rawMaterialForm.markAsPristine();
  }

  getSupplierCodes(): SupplierCodes[] {
    const suppliersList = this.itemDataCatalogService.getDataByKey("suppliers");
    let supplierCodes: SupplierCodes[] = [];
    if(suppliersList) {
      supplierCodes = suppliersList.map((sup: Suppliers) => ({ code: sup.id? sup.id.toString(): '', name: sup.name }));
    }
    return supplierCodes;
  }

  getRawMaterialFields(currentRawMaterial: RawMaterials = new RawMaterialsFe()): Partial<RawMaterials> {
    // Clono l'oggetto di destinazione per non mutare l'originale
    const updatedRawMaterial = { ...currentRawMaterial }; 

    // Recupero tutte le chiavi (nomi dei campi) presenti nel form
    const rawMaterialFieldsToMap = Object.keys(this.rawMaterialForm.controls);

    // Ciclo su tutte le chiavi trovate nel form
    rawMaterialFieldsToMap.forEach((key: string) => {
      const control = this.rawMaterialForm.get(key);

      if(control) {
        const val = control.value;
        const value = (val != "") ? val : (val || null);

        // Assegno il valore all'oggetto
        (updatedRawMaterial as any)[key] = value
      }
    })
    return updatedRawMaterial;
  }

  get id() {
    return this.rawMaterialForm.controls.id;
  }

  get name() {
    return this.rawMaterialForm.controls.name;
  }

  get quantity_alert() {
    return this.rawMaterialForm.controls.quantity_alert;
  }

  get unit() {
    return this.rawMaterialForm.controls.unit;
  }

  get cost() {
    return this.rawMaterialForm.controls.cost;
  }

  get default_supplier_id() {
    return this.rawMaterialForm.controls.default_supplier_id;
  }

  get stock_type() {
    return this.rawMaterialForm.controls.stock_type;
  }

  get auto_unload() {
    return this.rawMaterialForm.controls.auto_unload;
  }
}
